<template>
  <div>
    <v-toolbar elevation="0" class="mb-1 api-clients-toolbar px-2" rounded>
      <CreateClientDialog v-if="$canControlTenantConfig()" @clientCreated="onClientCreated" :accessPointId="accessPointId">
        <v-btn elevation="0" class="bg-primary" data-cy="apiClients-createClient-btn">
          <v-icon>mdi-key-plus</v-icon>
          <span class="hidden-sm-and-down">
            {{ $t('routes.apiClients.createClient') }}
          </span>
        </v-btn>
      </CreateClientDialog>

      <v-text-field
        :label="$t('routes.apiClients.searchClients')"
        variant="solo-filled"
        flat
        hide-details
        append-inner-icon="mdi-magnify"
        class="ml-2"
        @update:model-value="filterBySearchQuery"
        v-model="searchQuery"
      />

      <v-btn
        icon
        variant="text"
        class="ml-2"
        @click="showHelpDialog = true"
        data-cy="apiClients-help-btn"
      >
        <v-icon>mdi-help-circle-outline</v-icon>
        <v-tooltip activator="parent" location="bottom">
          {{ $t('routes.apiClients.helpTitle') }}
        </v-tooltip>
      </v-btn>
    </v-toolbar>

    <v-row>
      <v-col>
        <ApiClientsListView
          :clients="filteredClients"
          :loading="loading"
          :accessPointId="accessPointId"
          @deleteClient="onDeleteClient"
          @toggleStatus="onToggleStatus"
          @editClient="onEditClient"
        />
      </v-col>
    </v-row>

    <ClientCredentialsDialog
      v-model="showCredentialsDialog"
      :client="createdClient"
    />

    <EditClientDialog
      v-if="editingClient"
      v-model="showEditDialog"
      :client="editingClient"
      :access-point-id="accessPointId"
      @clientUpdated="onClientUpdated"
    />

    <HelpDialog v-model="showHelpDialog" />
  </div>
</template>

<script>
import { useApiClientStore } from '@/stores/ApiClientStore.js'
import ApiClientsListView from './components/ApiClientsListView.vue'
import CreateClientDialog from './components/dialogs/CreateClientDialog.vue'
import ClientCredentialsDialog from './components/dialogs/ClientCredentialsDialog.vue'
import EditClientDialog from './components/dialogs/EditClientDialog.vue'
import HelpDialog from './components/dialogs/HelpDialog.vue'
import { useAuthStore } from '@/stores/AuthStore.js';

export default {
  name: "ApiClientsIndex",
  components: {
    ApiClientsListView,
    CreateClientDialog,
    ClientCredentialsDialog,
    EditClientDialog,
    HelpDialog,
  },
  data() {
    return {
      searchQuery: '',
      loading: true,
      showCredentialsDialog: false,
      createdClient: null,
      showEditDialog: false,
      editingClient: null,
      showHelpDialog: false,
    };
  },
  created() {
    if (!this.$canControlTenantConfig()) {
      this.$router.push('/console/index');
    }
  },
  mounted() {
    this.fetchClients();
  },
  methods: {
    async fetchClients() {
      this.loading = true;
      try {
        const apiClientStore = useApiClientStore();
        await apiClientStore.fetchClients({ nodeId: this.accessPointId });
      } catch (error) {
        console.error('Error fetching API clients', error);
        this.$showMessage(this.$t('routes.apiClients.fetchError'), { type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    filterBySearchQuery(searchInput = '') {
      this.searchQuery = searchInput;
    },
    onClientCreated(client) {
      this.createdClient = client;
      this.showCredentialsDialog = true;
      this.$showMessage(this.$t('routes.apiClients.clientCreated'));
    },
    async onDeleteClient(clientId) {
      try {
        const payload = { clientId, nodeId: this.accessPointId };

        const apiClientStore = useApiClientStore();
        await apiClientStore.deleteClient(payload);
        this.$showMessage(this.$t('routes.apiClients.clientDeleted'));
      } catch (error) {
        console.error('Error deleting API client', error);
        const apiError = error?.response?.data?.error?.message;
        this.$showMessage(apiError || this.$t('routes.apiClients.deleteError'), { type: 'error' });
      }
    },
    async onToggleStatus(clientId, active) {
      try {
        const apiClientStore = useApiClientStore();
        await apiClientStore.updateStatus(clientId, active, this.accessPointId);
        this.$showMessage(
          active
            ? this.$t('routes.apiClients.clientEnabled')
            : this.$t('routes.apiClients.clientDisabled')
        );
      } catch (error) {
        console.error('Error updating API client status', error);
        const apiError = error?.response?.data?.error?.message;
        this.$showMessage(apiError || this.$t('routes.apiClients.updateError'), { type: 'error' });
      }
    },
    onEditClient(client) {
      this.editingClient = client;
      this.showEditDialog = true;
    },
    onClientUpdated() {
      this.showEditDialog = false;
      this.editingClient = null;
      this.$showMessage(this.$t('routes.apiClients.clientUpdated'));
    },
  },
  computed: {
    clients() {
      const apiClientStore = useApiClientStore();
      return apiClientStore.getClients;
    },
    filteredClients() {
      if (!this.searchQuery) {
        return this.clients;
      }
      const query = this.searchQuery.toLowerCase();
      return this.clients.filter((client) => {
        return (
          client.name?.toLowerCase().includes(query) ||
          client.client_id?.toLowerCase().includes(query) ||
          client.user_email?.toLowerCase().includes(query) ||
          client.user_name?.toLowerCase().includes(query)
        );
      });
    },
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
  },
}
</script>

<style scoped>
:deep(.v-theme--dark).api-clients-toolbar {
  background-color: #272727 !important;
}

:deep(.v-theme--light).api-clients-toolbar {
  background-color: white !important;
}
</style>
